import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { FiExternalLink, FiVolume2, FiVolumeX, FiPlay } from 'react-icons/fi';
import './CrazyMobelPage.css';

// Optimized Video Card Component with Lazy Loading
const VideoCard: React.FC<{ video: { id: number; videoUrl: string; title: string; thumbnail?: string }; index: number }> = ({ video, index }) => {
  const [isMuted, setIsMuted] = useState(true);
  const [isVisible, setIsVisible] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Intersection Observer - sadece görünür videolar yüklensin ve oynasın
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          } else {
            if (videoRef.current) {
              videoRef.current.pause();
            }
          }
        });
      },
      { threshold: 0.1, rootMargin: '50px' }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Video görünür olduğunda yükle ve oynat
  useEffect(() => {
    if (isVisible && videoRef.current) {
      videoRef.current.load();
      videoRef.current.play().catch(() => {});
    }
  }, [isVisible]);

  const toggleMute = () => {
    setIsMuted(!isMuted);
  };

  const handleLoadedData = () => {
    setIsLoaded(true);
  };

  const handleCanPlay = () => {
    if (videoRef.current && isVisible) {
      videoRef.current.play().catch(() => {});
    }
  };

  return (
    <motion.div 
      ref={containerRef}
      className="video-card"
      initial={{ opacity: 0, scale: 0.9 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 0.3, delay: index * 0.1 }}
    >
      <div className="video-container">
        {!isLoaded && (
          <div className="video-placeholder">
            <FiPlay className="play-icon" />
          </div>
        )}
        {isVisible && (
          <video
            ref={videoRef}
            src={video.videoUrl}
            loop
            muted={isMuted}
            playsInline
            preload="metadata"
            onLoadedData={handleLoadedData}
            onCanPlay={handleCanPlay}
            style={{ opacity: isLoaded ? 1 : 0 }}
          />
        )}
        <button className="video-mute-btn" onClick={toggleMute}>
          {isMuted ? <FiVolumeX /> : <FiVolume2 />}
        </button>
      </div>
      <h4>{video.title}</h4>
    </motion.div>
  );
};

const CrazyMobelPage: React.FC = () => {
  const { t } = useTranslation();

  // Crazy Möbel için çekilen videolar
  const videos = [
    { id: 1, videoUrl: '/crazy-video-1.mp4', title: 'Tanıtım Videosu 1' },
    { id: 2, videoUrl: '/crazy-video-2.mp4', title: 'Tanıtım Videosu 2' },
    { id: 3, videoUrl: '/crazy-video-3.mp4', title: 'Tanıtım Videosu 3' },
    { id: 4, videoUrl: '/crazy-video-4.mp4', title: 'Tanıtım Videosu 4' },
    { id: 5, videoUrl: '/crazy-video-5.mp4', title: 'Tanıtım Videosu 5' },
    { id: 6, videoUrl: '/crazy-video-6.mp4', title: 'Tanıtım Videosu 6' },
    { id: 7, videoUrl: '/crazy-video-7.mp4', title: 'Tanıtım Videosu 7' },
  ];

  return (
    <div className="crazymobel-page">
      {/* Hero Section */}
      <section className="crazymobel-hero">
        <div className="container">
          <motion.div
            className="hero-content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <img src="/crayzmobel.png" alt="Crazy Möbel Outlet" className="crazymobel-logo" />
            <h1>{t('crazyMobel.title')}</h1>
            <p className="hero-subtitle">{t('crazyMobel.subtitle')}</p>
            <a 
              href="https://crazy-moebel.de/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="visit-website-btn"
            >
              <FiExternalLink />
              {t('crazyMobel.visitWebsite')}
            </a>
          </motion.div>
        </div>
      </section>

      {/* Proje Detayları */}
      <section className="crazymobel-details section">
        <div className="container">
          <motion.div
            className="details-grid"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="detail-card">
              <h3>{t('crazyMobel.projectScope')}</h3>
              <p>{t('crazyMobel.projectScopeDesc')}</p>
            </div>
            <div className="detail-card">
              <h3>{t('crazyMobel.services')}</h3>
              <ul>
                <li>{t('crazyMobel.service1')}</li>
                <li>{t('crazyMobel.service2')}</li>
                <li>{t('crazyMobel.service3')}</li>
                <li>{t('crazyMobel.service4')}</li>
              </ul>
            </div>
            <div className="detail-card">
              <h3>{t('crazyMobel.results')}</h3>
              <p>{t('crazyMobel.resultsDesc')}</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Videolar Bölümü */}
      <section className="crazymobel-videos section">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="section-title">{t('crazyMobel.videosTitle')}</h2>
            <p className="section-subtitle">{t('crazyMobel.videosSubtitle')}</p>
          </motion.div>

          <motion.div 
            className="videos-grid"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            {videos.map((video, index) => (
              <VideoCard key={video.id} video={video} index={index} />
            ))}
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default CrazyMobelPage;
